import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { getTranslatedValue } from '@/utils/gtin-metadata';
import { GTINMetadata } from '@/contexts/item-context-types';
import { ItemDetail_GtinMetadataFieldsFragment } from '@/types';
import { RichInfoSection } from './rich-info-section';
import { RichInfoTable } from './rich-info-table';
import { StyleSheet } from 'react-native';
import { TableData } from '@/utils/salt-item';

interface Props {
  gtinMetadata: ItemDetail_GtinMetadataFieldsFragment;
  horizontal?: boolean;
}

type GTINMetadataKey = keyof GTINMetadata | 'gtin';

const ORDERED_GTIN_DETAILS: GTINMetadataKey[] = [
  'short_name',
  'brand_name',
  'manufacturer',
  'category',
  'manufacturer_product_code',
  'gtin',
  'pack_unit_quantity',
  'pack_size',
  'gross_weight',
  'net_weight',
  'origin_country',
  'kosher',
  'child_nutrition',
];

const ORDERED_GTIN_DIMENSIONS: GTINMetadataKey[] = [
  'ship_length',
  'ship_width',
  'ship_height',
  'ship_volume',
  'ship_ti_hi',
  'shelf_life_days',
  'storage_temperature',
];

const transformGTINMetadata = (
  keys: GTINMetadataKey[],
  metadata: GTINMetadata,
  gtin: string,
): TableData[] => {
  return keys.reduce<TableData[]>((acc, key) => {
    const translatedKey = i18n.t(`product_spec.${key.toLowerCase()}`);
    if (key === 'gtin') {
      acc.push({
        key: translatedKey,
        value: gtin,
      });
    } else if (key in metadata) {
      const value = metadata[key];
      if (typeof value === 'boolean' || typeof value === 'string') {
        acc.push({
          key: translatedKey,
          value: getTranslatedValue(key, value),
        });
      }
    }
    return acc;
  }, []);
};

export const RichInfoProductSpecs: React.FC<Props> = ({
  gtinMetadata,
  horizontal,
}) => {
  const { gtin, metadata } = gtinMetadata;
  const typedMetadata = metadata as GTINMetadata;

  const detailTableData = useMemo<TableData[]>(() => {
    return transformGTINMetadata(ORDERED_GTIN_DETAILS, typedMetadata, gtin);
  }, [gtin, typedMetadata]);

  const dimensionTableData = useMemo<TableData[]>(() => {
    return transformGTINMetadata(ORDERED_GTIN_DIMENSIONS, typedMetadata, gtin);
  }, [typedMetadata, gtin]);

  if (detailTableData.length === 0 && dimensionTableData.length === 0) {
    return null;
  }
  return (
    <>
      {detailTableData.length ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('product_specs')}
        >
          <RichInfoTable
            data={detailTableData}
            style={dimensionTableData.length ? styles.tableMargin : null}
          />
        </RichInfoSection>
      ) : null}
      {dimensionTableData.length ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('shipping_information')}
        >
          <RichInfoTable data={dimensionTableData} />
        </RichInfoSection>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  tableMargin: {
    marginBottom: 12,
  },
});
